import React, { useState, useEffect, useCallback } from 'react'
import { Box, Paper, Typography, Button, Tooltip } from '@mui/material'
import { Info } from '@mui/icons-material'
import { WebinarSession } from './WebinarSessionsList'
import { WebinarType } from './WebinarUtils'

export interface WebinarInfoPanelProps {
  categoryName: string
  description?: string
  schedule?: string
  sessions?: WebinarSession[]
  formatDateTime?: (isoString: string) => string
  allWebinarTypes?: WebinarType[]
}

const WebinarInfoPanel: React.FC<WebinarInfoPanelProps> = ({
  categoryName,
  description,
  schedule,
  sessions = [],
  formatDateTime = (date) => new Date(date).toLocaleString(),
  allWebinarTypes = [],
}) => {
  const [isStartingSoon, setIsStartingSoon] = useState(false)

  const nextSession = React.useMemo(() => {
    if (!sessions || sessions.length === 0) return null

    const now = new Date()

    const upcomingSessions = sessions.filter(
      (session) => new Date(session.start_time).getTime() > now.getTime()
    )

    if (upcomingSessions.length === 0) return null

    return upcomingSessions.sort(
      (a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
    )[0]
  }, [sessions])

  const isMastermindWebinar = useCallback((categoryName: string): boolean => {
    return categoryName.includes('Mastermind')
  }, [])

  const checkIfStartingSoon = useCallback(() => {
    if (!nextSession) {
      setIsStartingSoon(false)
      return
    }

    const now = new Date()
    const startTime = new Date(nextSession.start_time)
    const thirtyMinutesInMs = 30 * 60 * 1000

    setIsStartingSoon(startTime.getTime() - now.getTime() <= thirtyMinutesInMs)
  }, [nextSession])

  useEffect(() => {
    checkIfStartingSoon()

    const intervalId = setInterval(() => {
      checkIfStartingSoon()
    }, 60000)

    return () => clearInterval(intervalId)
  }, [nextSession, checkIfStartingSoon])

  const joinButton = React.useMemo(() => {
    if (!nextSession) return null

    const isMastermind = isMastermindWebinar(categoryName)

    if (isStartingSoon && nextSession.join_url) {
      return (
        <Button
          variant="contained"
          color="primary"
          size="small"
          href={nextSession.join_url}
          component="a"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ ml: 'auto', whiteSpace: 'nowrap' }}
        >
          Join Now
        </Button>
      )
    } else {
      const now = new Date()
      const startTime = new Date(nextSession.start_time)
      const timeUntilStart = startTime.getTime() - now.getTime()

      let tooltipMessage = `Next session starts at ${formatDateTime(nextSession.start_time)}`
      if (timeUntilStart > 0) {
        const daysUntil = Math.floor(timeUntilStart / (24 * 60 * 60 * 1000))
        const hoursUntil = Math.floor((timeUntilStart % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000))
        const minutesUntil = Math.floor((timeUntilStart % (60 * 60 * 1000)) / (60 * 1000))
        tooltipMessage += ` (in ${daysUntil > 0 ? `${daysUntil}d ` : ''}${hoursUntil > 0 ? `${hoursUntil}h ` : ''}${minutesUntil}m)`
      }

      if (isMastermind) {
        tooltipMessage += ` (Registration is managed manually)`
      }

      return (
        <Tooltip title={tooltipMessage} arrow placement="top">
          <span>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              disabled
              sx={{ ml: 'auto', whiteSpace: 'nowrap' }}
            >
              Join
            </Button>
          </span>
        </Tooltip>
      )
    }
  }, [nextSession, isStartingSoon, formatDateTime, categoryName, isMastermindWebinar])

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 2, backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
      <Box display="flex" alignItems="flex-start" gap={1}>
        <Info color="primary" sx={{ fontSize: 20, mt: 0.5 }} />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="body2" fontWeight="medium" color="text.primary">
            {categoryName}
          </Typography>
          <Typography variant="body2">{description}</Typography>
          {schedule && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Usually scheduled: {schedule}
            </Typography>
          )}
        </Box>
        {joinButton}
      </Box>
    </Paper>
  )
}

export default WebinarInfoPanel
